/**
 * 用户主题实例管理 /user-instances（旧 views/user-instances/index.vue）：
 * 关键词 / 主题 / 状态筛选、分页；详情、篇章、统计抽屉；暂停、恢复、标记完成、删除
 */
import { useEffect, useState } from 'react'
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  type MenuProps,
  type TableColumnsType
} from 'antd'
import { DownOutlined, SearchOutlined } from '@ant-design/icons'
import { coverageRate, formatDate, INSTANCE_STATUS_OPTIONS, instanceStatusBadge, instanceStatusLabel } from './constants'
import { useDeleteInstance, useInstanceAction, useInstanceList, useThemeOptions } from './hooks'
import { UserInstanceDetail } from './UserInstanceDetail'
import { EpisodesDrawer } from './EpisodesDrawer'
import { EpisodeContentDrawer } from './EpisodeContentDrawer'
import { StatsDrawer } from './StatsDrawer'
import type { InstanceAction, InstanceListQuery, UserInstance } from './types'
import styles from './UserInstances.module.css'

const ACTION_MESSAGES: Record<InstanceAction, string> = {
  pause: '已暂停学习',
  resume: '已恢复学习',
  complete: '已标记为完成'
}

const INITIAL_QUERY: InstanceListQuery = { page: 1, pageSize: 10, keyword: '' }

export function UserInstanceListPage() {
  const { message, modal } = App.useApp()
  const [keyword, setKeyword] = useState('')
  const [themeId, setThemeId] = useState<number>()
  const [status, setStatus] = useState<string>()
  const [query, setQuery] = useState<InstanceListQuery>(INITIAL_QUERY)
  const [detailInstance, setDetailInstance] = useState<UserInstance | null>(null)
  const [episodesInstanceId, setEpisodesInstanceId] = useState<number | null>(null)
  const [statsInstanceId, setStatsInstanceId] = useState<number | null>(null)
  const [content, setContent] = useState<{ instanceId: number; episodeId: number } | null>(null)
  const { data, isFetching, isError, refetch } = useInstanceList(query)
  const { data: themeOptions } = useThemeOptions()
  const runAction = useInstanceAction()
  const deleteInstance = useDeleteInstance()

  useEffect(() => {
    if (isError) message.error('获取实例列表失败')
  }, [isError, message])

  // 每次查询都带上当前的关键词和筛选条件；条件没变时重新拉取
  const search = (overrides: Partial<InstanceListQuery> = {}) => {
    const next = { ...query, keyword, themeId, status, page: 1, ...overrides }
    if (JSON.stringify(next) === JSON.stringify(query)) refetch()
    else setQuery(next)
  }

  const resetSearch = () => {
    setKeyword('')
    setThemeId(undefined)
    setStatus(undefined)
    search({ keyword: '', themeId: undefined, status: undefined })
  }

  const handleAction = async (action: InstanceAction, record: UserInstance) => {
    try {
      await runAction.mutateAsync({ id: record.id, action })
      message.success(ACTION_MESSAGES[action])
    } catch {
      message.error('操作失败')
    }
  }

  const confirmDelete = (record: UserInstance) => {
    modal.confirm({
      title: '确认删除',
      content: `确定要删除用户 "${record.userName || record.userId}" 的主题实例 "${record.themeTitle}" 吗？此操作将同时删除所有相关篇章，且不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteInstance.mutateAsync(record.id)
          message.success('用户实例删除成功')
        } catch (error) {
          console.error('删除失败:', error)
          message.error('删除用户实例失败')
        }
      }
    })
  }

  const moreActions = (record: UserInstance): MenuProps => ({
    items: [
      ...(record.status === 'active' ? [{ key: 'pause', label: '暂停学习' }] : []),
      ...(record.status === 'paused' ? [{ key: 'resume', label: '恢复学习' }] : []),
      ...(record.status === 'completed' ? [] : [{ key: 'complete', label: '标记完成' }]),
      { key: 'delete', label: '删除实例', danger: true }
    ],
    onClick: ({ key }) => {
      if (key === 'delete') confirmDelete(record)
      else handleAction(key as InstanceAction, record)
    }
  })

  const columns: TableColumnsType<UserInstance> = [
    {
      title: '用户',
      key: 'userName',
      width: 120,
      render: (_, record) => <Tag color="blue">{record.userName || `用户${record.userId}`}</Tag>
    },
    {
      title: '主题',
      key: 'themeTitle',
      width: 200,
      render: (_, record) => (
        <div>
          <strong>{record.themeTitle}</strong>
          <Typography.Text type="secondary" className={styles.subText}>
            {record.themeCode}
          </Typography.Text>
        </div>
      )
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Badge status={instanceStatusBadge(record.status)} text={instanceStatusLabel(record.status)} />
      )
    },
    {
      title: '学习进度',
      key: 'progress',
      width: 150,
      render: (_, record) => (
        <div>
          <Progress
            percent={record.progress || 0}
            size="small"
            status={record.status === 'completed' ? 'success' : 'active'}
          />
          <Typography.Text type="secondary" className={styles.subText}>
            {record.episodeCount} 篇章
          </Typography.Text>
        </div>
      )
    },
    {
      title: '词汇统计',
      key: 'wordStats',
      width: 150,
      render: (_, record) => (
        <div>
          <div>总词数: {record.totalWordCount || 0}</div>
          <div className={styles.small}>
            已知: {record.knownWordCount || 0}
            <span className={styles.gap}>覆盖率: {coverageRate(record)}%</span>
          </div>
        </div>
      )
    },
    {
      title: '时间信息',
      key: 'dates',
      width: 150,
      render: (_, record) => (
        <div className={styles.small}>
          <div>开始: {formatDate(record.startedAt)}</div>
          <div>活跃: {formatDate(record.lastActiveAt)}</div>
          {record.completedAt && <div>完成: {formatDate(record.completedAt)}</div>}
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setDetailInstance(record)}>
            详情
          </Button>
          <Button type="link" size="small" onClick={() => setEpisodesInstanceId(record.id)}>
            篇章
          </Button>
          <Button type="link" size="small" onClick={() => setStatsInstanceId(record.id)}>
            统计
          </Button>
          <Dropdown menu={moreActions(record)}>
            <Button size="small">
              更多 <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      )
    }
  ]

  return (
    <div>
      <Card title="用户主题实例管理">
        <Row gutter={[16, 12]} className={styles.filters}>
          <Col xs={24} md={6}>
            <Input.Search
              aria-label="搜索用户名或ID"
              placeholder="搜索用户名或ID"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onSearch={(value) => search({ keyword: value })}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              aria-label="选择主题"
              placeholder="选择主题"
              style={{ width: '100%' }}
              allowClear
              value={themeId}
              options={themeOptions}
              onChange={(value) => {
                setThemeId(value)
                search({ themeId: value })
              }}
            />
          </Col>
          <Col xs={12} md={5}>
            <Select
              aria-label="状态筛选"
              placeholder="状态筛选"
              style={{ width: '100%' }}
              allowClear
              value={status}
              options={INSTANCE_STATUS_OPTIONS}
              onChange={(value) => {
                setStatus(value)
                search({ status: value })
              }}
            />
          </Col>
          <Col xs={24} md={9}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={() => search({ page: query.page })}>
                查询
              </Button>
              <Button onClick={resetSearch}>重置</Button>
            </Space>
          </Col>
        </Row>

        <Table<UserInstance>
          rowKey="id"
          columns={columns}
          dataSource={data?.instances}
          loading={isFetching}
          scroll={{ x: 1200 }}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={(pagination) => search({ page: pagination.current ?? 1, pageSize: pagination.pageSize ?? 10 })}
        />
      </Card>

      <UserInstanceDetail instance={detailInstance} onClose={() => setDetailInstance(null)} />
      <EpisodesDrawer
        instanceId={episodesInstanceId}
        onClose={() => setEpisodesInstanceId(null)}
        onViewContent={(episode) => setContent({ instanceId: episode.userThemeInstanceId, episodeId: episode.id })}
      />
      <StatsDrawer instanceId={statsInstanceId} onClose={() => setStatsInstanceId(null)} />
      <EpisodeContentDrawer
        instanceId={content?.instanceId ?? null}
        episodeId={content?.episodeId ?? null}
        onClose={() => setContent(null)}
      />
    </div>
  )
}
